import Vue from 'vue';
import store from '@/store';
import { captureFatalError } from './captureFatalError';

/**
 * ERROR TOAST WITH SENTRY INTEGRATION
 * 
 * This utility displays error toast messages to users while simultaneously
 * capturing the error in Sentry with FATAL severity level.
 * 
 * USAGE IN VUE COMPONENTS:
 * 
 * Instead of:
 *   this.$toast.error(error.message || error);
 * 
 * Use:
 *   this.$errorToast(error);
 * 
 * With additional context:
 *   this.$errorToast(error, { additionalInfo: 'User was submitting form' });
 * 
 * The utility automatically captures:
 * - Component name
 * - Route name and path
 * - Route params
 * - Application token
 * - Error message and stack trace
 * 
 * All errors are sent to Sentry with 'fatal' severity and tagged for easy filtering.
 */

/**
 * Displays an error toast message and captures the error in Sentry with fatal severity
 * @param {Error|string} error - The error object or error message to display
 * @param {Object} context - Additional context to send to Sentry
 * @returns {void}
 */
export default function errorToast(error, context = {}) {
    // Get the error message
    const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || 'An unexpected error occurred';

    // Don't show toasts for 403 (Forbidden) errors.
    const status = error?.response?.status ?? error?.status;
    if (String(status) === '403' || String(status) === '401') {
        return;
    }

    // Get the calling component information if available
    const componentName = context.component || 'Unknown';
    const routeName = context.route?.name || 'Unknown';
    const routePath = context.route?.path || 'Unknown';
    const applicationToken = store.getters['application/applicationToken'];

    // Display the toast
    if (Vue.prototype.$toast && Vue.prototype.$toast.error) {
        Vue.prototype.$toast.error(errorMessage);
    }

    // Log to console for development visibility
    console.error('[TOAST ERROR]', error, { context });

    // Capture in Sentry with fatal severity
    const sentryError = error instanceof Error ? error : new Error(errorMessage);
    
    captureFatalError(sentryError, {
        contexts: {
            errorToast: {
                message: errorMessage,
                component: componentName,
                routeName,
                routePath,
                applicationToken,
                ...context
            }
        },
        tags: {
            errorType: 'toast-error',
            component: componentName,
            routeName
        }
    });
}

/**
 * Vue plugin installation method
 * Adds $errorToast to Vue prototype for use in components
 */
errorToast.install = function(Vue) {
    Vue.prototype.$errorToast = function(error, additionalContext = {}) {
        const context = {
            component: this.$options.name,
            route: {
                name: this.$route?.name,
                path: this.$route?.path,
                params: this.$route?.params
            },
            ...additionalContext
        };
        
        errorToast(error, context);
    };
};
